import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
	CONTENT_QUERY_ROOT,
	PUBLIC_CONTENT_QUERY_ROOT,
} from "./api/content-query-keys";
import { API_URL, openSseStream } from "./api/core";
import { notifyContentInvalidated } from "./content-invalidation";
import { createLiveRefresh } from "./live-refresh";

/**
 * Subscribes to server-sent invalidation events for the given org.
 * When the backend emits an `invalidate` event (e.g. after a template update via MCP),
 * this hook invalidates the corresponding React Query cache keys so the UI refreshes.
 */
// Start at 3s — matches the native EventSource retry default. The previous 1s
// start would hammer the server when Cloudflare returned transient 503s on a
// burst of route changes.
const INITIAL_RECONNECT_DELAY_MS = 3000;
const MAX_RECONNECT_DELAY_MS = 60_000;

export function useInvalidationSSE(orgSlug: string | null | undefined) {
	const queryClient = useQueryClient();
	const esRef = useRef<EventSource | null>(null);
	const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const reconnectDelay = useRef(INITIAL_RECONNECT_DELAY_MS);

	useEffect(() => {
		if (!orgSlug) return;

		let disposed = false;
		// Content changes can arrive faster than the reads they refresh can answer.
		const liveRefresh = createLiveRefresh(queryClient);

		function invalidateContent() {
			for (const queryKey of [
				[CONTENT_QUERY_ROOT],
				[PUBLIC_CONTENT_QUERY_ROOT],
				["event-view-match", orgSlug],
			]) {
				liveRefresh.refresh(queryKey);
			}
			notifyContentInvalidated();
		}

		async function connect() {
			// In the embedded iframe, append a fresh ?token= ticket so the stream
			// authenticates without a cookie. Minted per (re)connect so an expired
			// ticket self-heals on the next reconnect. Best-effort: on the normal web
			// this is a no-op and the session cookie authenticates.
			const es = await openSseStream(`${API_URL}/api/${orgSlug}/events`);
			if (disposed) {
				es.close();
				return;
			}
			esRef.current = es;

			// CONNECTING (0) → OPEN (1) means the stream succeeded. Reset backoff
			// here rather than on first message — a healthy stream may sit idle for
			// minutes before the first invalidation fires.
			es.onopen = () => {
				reconnectDelay.current = INITIAL_RECONNECT_DELAY_MS;
			};

			// Changes can land while the stream is disconnected. Once the server
			// has subscribed again, read current durable state to close that gap.
			es.addEventListener("connected", invalidateContent);

			es.addEventListener("invalidate", (e) => {
				try {
					const data = JSON.parse(e.data) as { keys?: string[] };
					if (data.keys) {
						for (const key of data.keys) {
							if (key === CONTENT_QUERY_ROOT) {
								invalidateContent();
							} else {
								liveRefresh.refresh([key]);
							}
							// A view write lands as `view:<key>`; the applied-views list
							// (`["views", org]`) carries every view's hash, so it refreshes
							// on any per-view key and the frames refetch changed bundles.
							if (key.startsWith("view:")) {
								liveRefresh.refresh(["views"]);
							}
						}
					}
				} catch {
					// Ignore malformed events
				}
			});

			es.onerror = () => {
				// EventSource fires `error` both for transport failures (where it would
				// auto-reconnect) and for terminal closes (4xx/5xx). We always close
				// and schedule our own reconnect with bounded exponential backoff so
				// burst failures don't pile up.
				es.close();
				esRef.current = null;

				if (disposed) return;

				reconnectTimeoutRef.current = setTimeout(() => {
					reconnectTimeoutRef.current = null;
					if (!disposed) {
						connect();
					}
				}, reconnectDelay.current);

				reconnectDelay.current = Math.min(
					reconnectDelay.current * 2,
					MAX_RECONNECT_DELAY_MS,
				);
			};
		}

		connect();

		return () => {
			disposed = true;
			liveRefresh.dispose();
			esRef.current?.close();
			esRef.current = null;
			if (reconnectTimeoutRef.current != null) {
				clearTimeout(reconnectTimeoutRef.current);
				reconnectTimeoutRef.current = null;
			}
			reconnectDelay.current = INITIAL_RECONNECT_DELAY_MS;
		};
	}, [orgSlug, queryClient]);
}
